import {default as SMUFactsheet} from '@manaakiwhenua/smap-smu-report/src/SMUFactsheet.svelte'
import {default as OneFactsheet} from '@manaakiwhenua/smap-smu-report/src/OneFactsheet.svelte'
import {ISMU, ISMUWithFactsheetType} from "../../lt-ng/soil-information";
import {Coordinate} from "ol/coordinate";
import {IUsageLoggingService} from '../IUsageLoggingService';
import {LTServices} from "../../lt-ng/lt-services";
import IMapService = LTServices.IMapService;

export interface ISmuFactsheetService {
    showSMUFactsheet(smu: ISMUWithFactsheetType, pinCoordinate: Coordinate, mapService: IMapService): SMUFactsheet;
    getFactsheetLink(smu: ISMUWithFactsheetType, pinCoordinate: Coordinate): SMUFactsheet;

    showFactsheetForQuery(search: { [key: string]: string }, mapService: IMapService): void;
}

function parseCoordinate(coordStr: string): Coordinate {
    if (!coordStr) return undefined;

    const pair = coordStr.split(',');

    if (pair.length !== 2) return;

    const coord = pair.map(parseFloat).filter(e => !isNaN(e));

    return coord.length === 2 ? coord : undefined;
}

export class SmuFactsheetService implements ISmuFactsheetService {
    public static $inject = ['usageLoggingService', '$location', '$rootScope'];

    constructor(protected usageLoggingService: IUsageLoggingService,
                protected $location: ng.ILocationService,
                protected $rootScope: ng.IRootScopeService) {

    }

    protected closeReport: () => void = () => {};

    public showSMUFactsheet(smu: ISMUWithFactsheetType, pinCoordinate: Coordinate, mapService: IMapService) {
        // I would have injected mapService into SmuFactsheetService constructor, but it causes a circular dependency issue in SMapAppConfig
        document.querySelector('body')
            .classList.add('report-active');

        const container = document.querySelector('#report-container') as HTMLElement;
        const inner = document.createElement('div');
        inner.classList.add('fullscreen-report');
        inner.setAttribute('tabindex', '0');
        container.appendChild(inner);

        container.classList.remove('hidden');

        let licence = undefined;

        if ((window as any).LTConfiguration) {
            const conf: any = (window as any).LTConfiguration;
            const maybeLicence = conf && conf.license || {};
            const keys = [...Object.keys(maybeLicence)];

            licence = keys.length && keys[0] !== 'cc' ? maybeLicence[keys[0]] : licence;
        }

        const FS = smu.factsheetType === "teReoSibling" ? OneFactsheet : SMUFactsheet;

        const fs = new FS({
            target: inner,
            props: {
                gisLayer: smu.gislayer,
                soilMapUnitCode: smu.soilmapuc,
                siblingNumber: smu.siblingNumber,
                reportLink: this.getFactsheetLink(smu, pinCoordinate),
                objectId: smu.objectId,
                licence,
                pinCoordinate,
                // factsheetApiUrl: (window['LTConfiguration'] || {}).factsheetsAPI
                factsheetApiUrl: `${new URL('/factsheet-api/', window.location.href)}`
            }
        });

        fetch(window.location.pathname + '/smuPrintUrl')
            .then(r => r.json())
            .then(printPage => {
                const printLink = printPage ? this.getPrintLink(printPage, smu, pinCoordinate) : '';
                fs.$set({
                    printLink
                });
            });

        // disable the map's keyboard while the report is active
        const keyboardDisabled = mapService.mapPromise.then(() => {
            mapService.reportOpen = true;
            inner.focus();
        });

        this.closeReport = () => {
            document.querySelector('body')
                .classList.remove('report-active');
            keyboardDisabled.then(() => {
                mapService.reportOpen = false; // re-enable the map's keyboard
            });
            fs.$destroy();
            container.innerHTML = '';
            container.classList.add('hidden');
            this.closeReport = () => {};
        };

        // fs.$on('closeReport', this.closeReport);
        fs.$on('closeReport', () => {
            this.$rootScope.$apply(() => {
                this.$location.search({});
            });
        });

        this.usageLoggingService.logWithAnalytics((smu.factsheetType || "smu" ) + 'Factsheet', `show`, `${smu.gislayer}: ${smu.soilmapuc}`);
        return fs;
    }

    protected smuParams(smu: ISMU, pinCoordinate: Coordinate) : URLSearchParams {
        const params = new URLSearchParams();

        Object.keys(smu).forEach(k => {
            params.set(k, smu[k]);
        });

        if (pinCoordinate) {
            params.set('pinCoordinate', pinCoordinate.join(','));
        }

        return params;
    }

    showFactsheetForQuery(search, mapService: IMapService) {
        const coordinateParam = search['pinCoordinate'];
        const coordinate = parseCoordinate(coordinateParam);
        const smu: ISMUWithFactsheetType = {
            gislayer: search.gislayer,
            soilmapuc: search.soilmapuc,
            factsheetType: search.factsheetType || undefined,
            siblingNumber: search.siblingNumber || undefined,
            objectId: search.objectId || undefined
        };

        this.closeReport();
        if (smu.gislayer && smu.soilmapuc) {
            this.showSMUFactsheet(smu, coordinate, mapService);
        }
    }

    getFactsheetLink(smu: ISMUWithFactsheetType, pinCoordinate: Coordinate): string {
        const reportLink = new URL(`${window.location}`);
        reportLink.search = `${this.smuParams(smu, pinCoordinate)}`;
        return `${reportLink}`;
    }

    private getPrintLink(printPage, smu: ISMUWithFactsheetType, pinCoordinate: Coordinate) {
        const printLink = new URL(printPage);
        printLink.search = `${this.smuParams(smu, pinCoordinate)}`;
        return `${printLink}`;
    }
}
